import React, { useEffect } from 'react'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import { useForm } from 'react-hook-form'
import { Box, Button, Flex, Input, Tooltip } from 'pcln-design-system'
import { CONTENT, EMAIL_REGEXP } from './signup-content'
import analytics from '../../utils/analytics'
import { INVALID, VALID } from '../../constants/analytics'

const EmailBannerWrapper = styled(Flex)`
  display: none;

  ${themeGet('mediaQueries.md')} {
    display: inline-block;
    width: auto;
  }

  ${themeGet('mediaQueries.xxl')} {
    display: flex;
  }
`

const EmailFormWrapper = styled(Flex)`
  ${themeGet('mediaQueries.md')} {
    margin-left: 0;
  }

  ${themeGet('mediaQueries.xxl')} {
    margin-left: ${themeGet('space.4')}px;
  }
`

const EmailInput = styled(Input)`
  border-radius: 2px;
  display: inline;
  height: 40px;
  width: 308px;
`

type EmailSignUpFormTypes = {
  category: string
  children: React.ReactNode
  apiError?: Error | null
  isLoading: boolean
  submitEmail: ({ signUpPromotion }: { signUpPromotion: string }) => void
}

type EmailDataType = {
  signUpPromotion: string
}

export default function EmailSignUpForm({
  category,
  children,
  apiError,
  submitEmail,
  isLoading
}: EmailSignUpFormTypes) {
  const { errors, handleSubmit, register } = useForm<EmailDataType>({
    mode: 'onSubmit'
  })

  const errorMessage = errors.signUpPromotion
    ? 'Please enter a valid email.'
    : apiError?.message

  useEffect(() => {
    analytics.fireEvent({
      action: 'Email Signup',
      category,
      label: errorMessage ? INVALID : VALID
    })
  }, [category, errorMessage])

  return (
    <form onSubmit={handleSubmit(submitEmail)}>
      <EmailBannerWrapper alignItems="center" justifyContent="center" py={2}>
        <Box pb={2} pt={[null, null, 2]} width={[1, null, 'auto']}>
          {children}
        </Box>
        <EmailFormWrapper
          alignItems="center"
          justifyContent="center"
          ml={4}
          py={2}
        >
          <Box>
            <EmailInput
              aria-label="Promotion Input"
              aria-describedby="promotion_error"
              id="signUpPromotion"
              name="signUpPromotion"
              color={errorMessage ? 'red' : ''}
              ref={register({
                required: true,
                pattern: EMAIL_REGEXP
              })}
              placeholder={CONTENT.placeHolder}
            />
            <Box id="promotion_error">
              {errorMessage && (
                <Tooltip bottom right color="error" zIndex={1}>
                  {errorMessage}
                </Tooltip>
              )}
            </Box>
          </Box>
          <Box>
            <Button
              disabled={isLoading}
              ml={3}
              px={3}
              variation="outline"
              data-testid="email-signup-button"
              type="submit"
            >
              {isLoading ? CONTENT.buttonSubmittingText : CONTENT.buttonText}
            </Button>
          </Box>
        </EmailFormWrapper>
      </EmailBannerWrapper>
    </form>
  )
}
